import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useParams } from "react-router-dom";

import { invalidateFavoritesList } from "@/queries/favorites";

const matchesEntityScope = (
  queryKey: readonly unknown[],
  workspaceId: string | undefined,
  folderId: number | null,
) => {
  if (queryKey[0] !== "folder-entities") {
    return false;
  }
  if (workspaceId !== undefined && String(queryKey[1]) !== workspaceId) {
    return false;
  }

  const qFolderId = (queryKey[2] ?? null) as number | null;
  const qFlatten = queryKey[8] as boolean | undefined;

  if (qFlatten === true) {
    return true;
  }
  if (qFolderId === folderId) {
    return true;
  }
  return false;
};

/**
 * Plain helper for non-hook contexts (e.g. MobX mutation factories that
 * receive a QueryClient by reference). Hook contexts should prefer
 * useInvalidateFolderEntitiesForEntity.
 */
export const invalidateFolderEntitiesForEntity = (
  queryClient: QueryClient,
  workspaceId: string | number | undefined,
  folderId: number | null | undefined,
) => {
  const wsId = workspaceId !== undefined ? String(workspaceId) : undefined;
  const targetFolderId = folderId ?? null;

  queryClient.invalidateQueries({
    predicate: query => matchesEntityScope(query.queryKey, wsId, targetFolderId),
  });

  // Favorites hydrate display names from live entities — keep them in sync on rename/move/etc.
  invalidateFavoritesList(queryClient, workspaceId);
};

/**
 * Returns a function that invalidates only the folder-entities listings
 * containing a given entity: the navigation flatten query plus the listing
 * of the entity's own folder. Other folders, search results, and filtered
 * variants are left alone. workspaceId is pulled from the current route.
 */
export const useInvalidateFolderEntitiesForEntity = () => {
  const queryClient = useQueryClient();
  const { workspaceId } = useParams();

  return useCallback(
    (folderId: number | null | undefined) => {
      invalidateFolderEntitiesForEntity(queryClient, workspaceId, folderId);
    },
    [queryClient, workspaceId],
  );
};
